import { z } from 'zod';
import { refineVatTriplet } from '../fiscal/vat-triplet';
import { posDdtOfferSchema } from './ddt-offers';
import { posDdtDocumentSerialSchema } from './ddt-identifiers';

/** Transport document snapshots: a missing amount is never a zero price. */
export const posActiveDdtItemInputSchema = z.object({
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullish(),
  product_name: z.string().trim().max(255).nullish(),
  sku: z.string().trim().max(100).nullish(),
  supplier_id: z.string().uuid().nullish(),
  supplier_sku: z.string().trim().max(120).nullish(),
  ean: z.string().trim().max(100).nullish(),
  offer_snapshot: posDdtOfferSchema.nullish(),
  quantity: z.number().finite().positive(),
  unit_of_measure: z.string().trim().min(1).max(20).nullish(),
  unit_price: z.number().finite().nonnegative().nullish(),
  vat_rate: z.number().finite().min(0).max(100).nullish(),
  vat_code: z.string().trim().min(1).max(20).nullish(),
  vat_nature: z.string().regex(/^N[1-7](?:\.[1-9])?$/).nullish(),
  riferimento_normativo: z.string().trim().max(255).nullish(),
  discount_percent: z.number().finite().min(0).max(100).nullish(),
  notes: z.string().max(20000).nullish(),
  serial_number: z.string().trim().max(255).nullish(),
  lot_number: z.string().trim().max(255).nullish(),
  serial_numbers: z.array(z.string().trim().min(1).max(255)).optional(),
  serial_selections: z.array(z.object({
    serial_id: z.string().uuid().optional(),
    serial_number: z.string().trim().min(1).max(255),
  }).strict()).optional(),
}).strict().superRefine((item, ctx) => {
  // Regola unica della terna (shared `vat-triplet`): natura e riferimento
  // solo a IVA 0%. Ad aliquota 0 nulla è obbligatorio sulla riga: quando la
  // riga non li porta li eredita dal prodotto (`pos_products`) nel service.
  refineVatTriplet(item, ctx, { zeroRate: 'none' });
});
export type PosActiveDdtItemInput = z.infer<typeof posActiveDdtItemInputSchema>;

const wireNumeric = z.union([z.string(), z.number()]).nullable();
export const posActiveDdtItemReadSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
  variant_attributes: z.record(z.string(), z.string()).nullable(),
  product_name: z.string(),
  sku: z.string().nullable(),
  supplier_id: z.string().uuid().nullish(),
  supplier_sku: z.string().nullable().optional(),
  ean: z.string().nullable().optional(),
  lot_number: z.string().nullable().optional(),
  notes: z.string().nullish(),
  serial_numbers: z.array(z.string()).nullish(),
  document_serials: z.array(posDdtDocumentSerialSchema).nullish(),
  offer_snapshot: posDdtOfferSchema.nullish(),
  quantity: wireNumeric,
  unit_price: wireNumeric,
  vat_rate: wireNumeric,
  vat_code: z.string().nullable(),
  vat_nature: z.string().nullable(),
  riferimento_normativo: z.string().nullable(),
  discount_percent: wireNumeric,
}).passthrough();

type Numeric = number | string | null | undefined;
function numeric(value: Numeric): number | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Importi di una riga DDT attiva a partire dal prezzo unitario NETTO
 * persistito su `pos_ddt_details.unit_price` (è così che l'emissione lo
 * scrive e la stampa lo legge): imponibile → IVA aggiunta sopra, senza
 * arrotondamento al centesimo per riga (lo applica la presentazione).
 *
 * NON è il calcolo della conversione DDT → vendita: quella usa `calculateVat`
 * come la cassa (prezzo di listino = LORDO, IVA scorporata).
 */
export function activeDdtLineAmounts(quantity: Numeric, unitPrice: Numeric, vatRate: Numeric, discountPercent: Numeric = 0) {
  const qty = numeric(quantity);
  const price = numeric(unitPrice);
  const rate = numeric(vatRate);
  const discount = Math.max(0, Math.min(100, numeric(discountPercent) ?? 0));
  const net = qty === null || price === null ? null : qty * price * (1 - discount / 100);
  const tax = net === null || rate === null ? null : net * (rate / 100);
  return { net, tax, total: net === null || tax === null ? null : net + tax };
}